import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  SheetMirrorColumnDto,
  SheetMirrorDupRefDto,
  SheetMirrorRowDto,
  SheetMirrorTabKey,
} from '@bhops/shared';
import { sheetMirrorApi } from '@/api/sheetMirror';
import { extractApiError } from '@/api/client';
import { Input, Select } from '@/components/Form';
import { Modal } from '@/components/Modal';
import { Icon } from '@/components/Icon';
import {
  SheetTable,
  SheetTd,
  SheetTh,
  sheetTableWrap,
  sortSheetRows,
  type SheetSort,
} from './sheetTable';
import { currentMonthKey } from './sheetDates';
import { MARK_SIZE, MethodMark } from './sheetMarks';

// BUSINESS '고객사 인바운드 현황'(2026-10-23) — 오너: "자동 IB 는 읽기 전용", "자동 IB 가 생긴 시점부터".
// 구글 시트 '인바운드 문의 현황' 의 달 탭 '자동 IB(YY.MM)'(26.03~)를 그대로 보여 준다.
//   · 칸 · 순서 · 너비는 시트 그대로(스크립트가 시트에서 읽어 온다). 콘솔에서는 고치지 않는다.
//   · 이번 달 탭은 1분마다, 지난 달 탭은 몇 분에 한 번씩 새로 받는다.
//   · '번호 중복 및 오류' 칸(2026-10-23 오너): 비유효 · 번호오류 줄은 흐리게, 중복 줄은 어느 줄과
//     겹치는지(서버가 같은 번호 · 상호로 찾은 dupOf)를 칸 안에 보이고 상세에서 그 줄로 옮겨 간다.
//   · 머리글을 누르면 콘솔에서만 정렬(시트 순서는 그대로).

const GROUP = 'auto-ib' as const;

/** 표에서 왼쪽 정렬로 길게 보여 줄 칸 */
const LONG_KEYS = new Set(['상담 내용']);
const METHOD = '문의 방법';
const ROUTE = '유입경로';
const OWNER = '담당자';
const NAME = '기업/상호';

/** 'A열 번호'처럼 머리글이 숫자뿐인 칸 — 시트의 줄 번호라 화면에 안 보인다. */
const isCounter = (c: SheetMirrorColumnDto) => /^\d*$/.test(c.key);
const dupKey = (cols: SheetMirrorColumnDto[]) =>
  cols.find((c) => c.key.startsWith('번호 중복'))?.key;
/** 중복 칸 콘솔 폭(px, 여백 포함) */
const DUP_WIDTH = 116;
/**
 * 칸 기본 폭(px, 여백 포함) — 2026-10-23 오너가 운영 화면(원본 시트)에서 맞춘 폭("내가 다시 조정해둔걸 기준으로해").
 * 오너 화면 캡처의 칸 경계에서 쟀다(화면 배율 1.1 — 240px 검색 칸이 264px). 여기 없는 칸은 시트 열 너비.
 */
const DEFAULT_WIDTHS: Record<string, number> = {
  '문의 일자': 48,
  주차: 40,
  수: 42,
  '기업/상호': 136,
  '문의 방법': 86,
  유입경로: 187,
  메뉴: 129,
  지역1: 40,
  지역2: 105,
  전화번호: 108,
  담당자: 53,
  'B2B 유효': 70,
  '가능 지역': 70,
  '창업 여부': 53,
  관심사: 54,
  홀직원수: 53,
  '희망 시기': 53,
  데모: 53,
  '매장 사진': 54,
  '1) 카톡 발송': 61,
};
type Flag = 'dup' | 'invalid' | null;
/** '중복' → dup, 그 밖에 적힌 것(비유효 · 번호오류) → invalid */
const flagOf = (r: SheetMirrorRowDto, key: string | undefined): Flag => {
  const v = key ? (r.cells[key] ?? '').trim() : '';
  return !v ? null : v.includes('중복') ? 'dup' : 'invalid';
};
/** '26.09 5행' — 상대 줄을 짧게 */
const refLabel = (d: SheetMirrorDupRefDto) =>
  d.tabKey === 'demo-conversion'
    ? `데모 ${d.rowNumber}행`
    : `${monthOfTab(d.sheetName)} ${d.rowNumber}행`;
const monthOfTab = (sheetName: string) => /\((\d\d\.\d\d)\)/.exec(sheetName)?.[1] ?? sheetName;

export function InboundPage() {
  const monthsQ = useQuery({
    queryKey: ['sheet-mirror', 'groups', GROUP],
    queryFn: () => sheetMirrorApi.months(GROUP),
    refetchInterval: 60_000,
    retry: false,
  });
  const months = monthsQ.data ?? [];
  const [picked, setPicked] = useState<SheetMirrorTabKey | null>(null);
  const key = picked ?? currentMonthKey(months);

  const q = useQuery({
    queryKey: ['sheet-mirror', key],
    queryFn: () => sheetMirrorApi.get(key!),
    enabled: !!key,
    refetchInterval: 60_000,
    retry: false,
  });
  const tab = q.data;

  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('');
  const [route, setRoute] = useState('');
  const [owner, setOwner] = useState('');
  const [flagOnly, setFlagOnly] = useState<Flag>(null);
  const [sort, setSort] = useState<SheetSort>(null);
  /**
   * 강조한 중복 상대 줄(2026-10-23 오너 "이거 누르면 중복된 행 하이라이트") — 그 달 탭의 줄들, 그중 focus 로 스크롤.
   * 달 탭을 직접 바꾸면 풀린다.
   */
  const [hl, setHl] = useState<{
    tabKey: string;
    rowKeys: string[];
    focus: string;
    seq: number;
  } | null>(null);
  const tableWrap = useRef<HTMLDivElement>(null);
  const scrolled = useRef(0);
  // 처음엔 시트 칸을 모두(2026-10-23 오너 "지역 1 2 있는데 지역 1만" — 그 달에 빈 칸도 시트 구조 그대로)
  const [hideEmpty, setHideEmpty] = useState(false);
  const [open, setOpen] = useState<SheetMirrorRowDto | null>(null);

  const columns = useMemo(() => tab?.columns ?? [], [tab]);
  const allRows = useMemo(() => tab?.rows ?? [], [tab]);
  const dup = dupKey(columns);
  const has = (k: string) => columns.some((c) => c.key === k);
  // '번호 중복 및 오류' 칸만 콘솔에서 넓게 — '중복 → 26.09 5행'(겹치는 줄)이 잘리지 않게. 시트 너비는 그대로.
  const widths = useMemo(
    () => (dup ? { ...DEFAULT_WIDTHS, [dup]: DUP_WIDTH } : DEFAULT_WIDTHS),
    [dup],
  );

  const tableCols = useMemo(
    () =>
      columns.filter(
        (c) =>
          !c.hidden &&
          !isCounter(c) &&
          (!hideEmpty || allRows.some((r) => (r.cells[c.key] ?? '').trim())),
      ),
    [columns, allRows, hideEmpty],
  );

  const methods = useMemo(() => countBy(allRows, METHOD), [allRows]);
  const routes = useMemo(() => countBy(allRows, ROUTE), [allRows]);
  const owners = useMemo(() => countBy(allRows, OWNER), [allRows]);
  const dupCount = allRows.filter((r) => flagOf(r, dup) === 'dup').length;
  const invalidCount = allRows.filter((r) => flagOf(r, dup) === 'invalid').length;

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = allRows.filter((r) => {
      if (method && (r.cells[METHOD] ?? '') !== method) return false;
      if (route && (r.cells[ROUTE] ?? '') !== route) return false;
      if (owner && (r.cells[OWNER] ?? '') !== owner) return false;
      if (flagOnly && flagOf(r, dup) !== flagOnly) return false;
      if (s && !Object.values(r.cells).some((v) => v.toLowerCase().includes(s))) return false;
      return true;
    });
    // 기본은 최근 문의 먼저(시트 아래쪽부터) — 머리글을 눌러 다른 순서로(2026-10-23 오너 체크박스 뺌)
    return sortSheetRows([...list].reverse(), sort);
  }, [allRows, search, method, route, owner, flagOnly, dup, sort]);

  // 강조한 줄이 화면에 그려지면 그 줄로 한 번 스크롤
  useEffect(() => {
    if (!hl || tab?.key !== hl.tabKey || scrolled.current === hl.seq) return;
    const el = tableWrap.current?.querySelector<HTMLElement>(
      `[data-row-key="${CSS.escape(hl.focus)}"]`,
    );
    if (!el) return;
    scrolled.current = hl.seq;
    el.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  }, [hl, tab, rows]);

  /**
   * 중복 상대 줄을 강조 — 같은 달이면 그 자리에서, 다른 달이면 그 달로 넘어가서.
   * 데모-계약 전환 줄이면 그 화면으로(?hl=줄 열쇠).
   */
  const goTo = (from: SheetMirrorRowDto, d: SheetMirrorDupRefDto) => {
    setOpen(null);
    if (d.tabKey === 'demo-conversion') {
      window.location.assign(`/business/demo-conversion?hl=${encodeURIComponent(d.rowKey)}`);
      return;
    }
    const rowKeys = (from.dupOf ?? []).filter((x) => x.tabKey === d.tabKey).map((x) => x.rowKey);
    if (d.tabKey !== key) {
      pickMonth(d.tabKey);
      setSearch('');
    } else if (!rowKeys.every((k) => rows.some((r) => r.rowKey === k))) {
      // 걸러져 안 보이는 줄이면 거르기를 푼다
      setMethod('');
      setRoute('');
      setOwner('');
      setFlagOnly(null);
      setSearch('');
    }
    setHl({ tabKey: d.tabKey, rowKeys, focus: d.rowKey, seq: Date.now() });
  };

  const pickMonth = (k: SheetMirrorTabKey) => {
    setPicked(k);
    setMethod('');
    setRoute('');
    setOwner('');
    setFlagOnly(null);
  };

  if (monthsQ.isLoading) return <div style={page}>불러오는 중…</div>;
  if (monthsQ.isError || !months.length)
    return (
      <div style={page}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          인바운드 현황(자동)
        </h1>
        <div style={notice} data-testid="inbound-not-connected">
          {monthsQ.isError
            ? extractApiError(monthsQ.error)
            : '아직 시트와 연결되지 않았습니다. 시트의 앱스 스크립트가 한 번 실행되면 보입니다.'}
        </div>
      </div>
    );

  return (
    <div style={page}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 'auto' }}>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            인바운드 현황(자동)
          </h1>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
            {tab ? (
              <>
                구글 시트 &lsquo;{tab.sheetName}&rsquo; 탭을 읽기만 합니다. 고치기는 시트에서 해
                주세요.{' '}
                {tab.lastSyncAt
                  ? `마지막으로 받은 때는 ${agoText(tab.lastSyncAt)}입니다.`
                  : '아직 받은 적이 없습니다.'}
              </>
            ) : (
              '구글 시트 자동 IB 탭'
            )}
          </div>
        </div>
        {tab && (
          <a href={tab.sheetUrl} target="_blank" rel="noreferrer" style={linkBtn}>
            시트 열기
          </a>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="tablist" aria-label="달">
        {months.map((m) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={m.key === key}
            onClick={() => {
              pickMonth(m.key);
              setHl(null);
            }}
            className="kuun-sheet-kpi"
            style={{ ...chip, ...(m.key === key ? chipOn : null) }}
            data-testid={`inbound-month-${m.key}`}
          >
            {monthLabel(m.month)} <span style={{ opacity: 0.7 }}>{m.rowCount}</span>
          </button>
        ))}
      </div>

      {q.isError && <div style={notice}>{extractApiError(q.error)}</div>}
      {tab?.lastError && <div style={notice}>{tab.lastError}</div>}

      {tab && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => {
                setMethod('');
                setFlagOnly(null);
              }}
              className="kuun-sheet-kpi"
              style={{ ...kpi, ...(!method && !flagOnly ? kpiOn : null) }}
            >
              <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                전체 문의
              </span>
              <strong style={{ fontSize: 18 }} data-testid="inbound-total">
                {allRows.length}
              </strong>
            </button>
            {methods.map(([m, n]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMethod(method === m ? '' : m)}
                className="kuun-sheet-kpi"
                style={{ ...kpi, ...(method === m ? kpiOn : null) }}
                aria-pressed={method === m}
              >
                <span className="kuun-caption" style={kpiLabel}>
                  <MethodMark method={m} />
                  {m}
                </span>
                <strong style={{ fontSize: 18 }}>{n}</strong>
              </button>
            ))}
            {dup &&
              (
                [
                  ['dup', '중복', dupCount, `시트 '${dup}' 칸이 '중복'인 문의`],
                  [
                    'invalid',
                    '비유효 · 오류',
                    invalidCount,
                    `시트 '${dup}' 칸이 비유효이거나 번호 오류인 문의`,
                  ],
                ] as const
              ).map(([f, label, n, title]) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFlagOnly(flagOnly === f ? null : f)}
                  className="kuun-sheet-kpi"
                  style={{ ...kpi, ...(flagOnly === f ? kpiOn : null) }}
                  aria-pressed={flagOnly === f}
                  title={title}
                  data-testid={`inbound-kpi-${f}`}
                >
                  <span className="kuun-caption" style={kpiLabel}>
                    <Icon
                      name={f === 'dup' ? 'copy' : 'x'}
                      size={MARK_SIZE}
                      color={f === 'dup' ? 'var(--brand)' : 'var(--fg-danger)'}
                      strokeWidth={2.25}
                    />
                    {label}
                  </span>
                  <strong style={{ fontSize: 18 }}>{n}</strong>
                </button>
              ))}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              placeholder="상호, 전화번호, 상담 내용 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
              aria-label="검색"
            />
            {has(ROUTE) && routes.length > 0 && (
              <Select
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                aria-label="유입경로"
                style={{ width: 200 }}
              >
                <option value="">유입경로 전체</option>
                {routes.map(([r, n]) => (
                  <option key={r} value={r}>
                    {r} ({n})
                  </option>
                ))}
              </Select>
            )}
            {has(OWNER) && owners.length > 0 && (
              <Select
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                aria-label="담당자"
                style={{ width: 140 }}
              >
                <option value="">담당자 전체</option>
                {owners.map(([o, n]) => (
                  <option key={o} value={o}>
                    {o} ({n})
                  </option>
                ))}
              </Select>
            )}
            <label className="kuun-caption" style={checkLabel}>
              <input
                type="checkbox"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
              />
              빈 칸 숨기기
            </label>
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              {rows.length}건
            </span>
          </div>

          <div style={sheetTableWrap} ref={tableWrap}>
            <SheetTable columns={tableCols} widths={widths}>
              <thead>
                <tr>
                  {tableCols.map((c) => (
                    <SheetTh key={c.key} col={c} widths={widths} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const flag = flagOf(r, dup);
                  const lit = hl?.tabKey === key && hl.rowKeys.includes(r.rowKey);
                  return (
                    <tr
                      key={r.rowKey}
                      onClick={() => setOpen(r)}
                      style={{ cursor: 'pointer' }}
                      data-testid={`inbound-row-${r.rowNumber}`}
                      data-row-key={r.rowKey}
                      data-flag={flag ?? undefined}
                      data-highlight={lit || undefined}
                    >
                      {tableCols.map((c) => (
                        <SheetTd
                          key={c.key}
                          long={LONG_KEYS.has(c.key)}
                          bg={r.bg?.[c.key]}
                          title={
                            c.key === dup && flag === 'dup'
                              ? dupTitle(r.dupOf)
                              : (r.cells[c.key] ?? '')
                          }
                          // 강조한 중복 상대 줄이 먼저, 그다음 비유효 · 번호오류 줄은 흐리게(유효 문의가 먼저 눈에)
                          style={lit ? litCell : flag === 'invalid' ? dim : undefined}
                        >
                          {r.cells[c.key] ?? ''}
                          {c.key === dup &&
                            flag === 'dup' &&
                            (r.dupOf?.length ? (
                              <button
                                type="button"
                                style={dupRefBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goTo(r, r.dupOf![0]!);
                                }}
                                data-testid={`inbound-dup-link-${r.rowNumber}`}
                              >
                                → {refLabel(r.dupOf[0]!)}
                              </button>
                            ) : (
                              <span style={{ color: 'var(--fg-muted)' }}> → ?</span>
                            ))}
                        </SheetTd>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </SheetTable>
          </div>
        </>
      )}

      {open && tab && (
        <Modal
          open
          onClose={() => setOpen(null)}
          title={`${open.cells[NAME] || '문의'} · ${tab.sheetName} ${open.rowNumber}행`}
          width={640}
          footer={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <span
                className="kuun-caption"
                style={{ color: 'var(--fg-muted)', marginRight: 'auto' }}
              >
                읽기 전용입니다. 고치기는 시트에서 해 주세요.
              </span>
              <a
                href={`${tab.sheetUrl}${tab.sheetUrl.includes('#') ? '&' : '#'}range=A${open.rowNumber}`}
                target="_blank"
                rel="noreferrer"
                style={linkBtn}
              >
                시트에서 이 줄 열기
              </a>
            </div>
          }
        >
          {flagOf(open, dup) === 'dup' && (
            <div style={dupBox} data-testid="inbound-dup-refs">
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {open.dupOf?.length
                  ? `${open.dupOf[0]!.by === 'phone' ? '같은 전화번호' : '같은 상호'}로 겹친 문의`
                  : '중복 상대를 찾지 못했습니다'}
              </div>
              {open.dupOf?.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {open.dupOf.map((d) => (
                    <button
                      key={d.tabKey + d.rowKey}
                      type="button"
                      style={refBtn}
                      onClick={() => goTo(open, d)}
                    >
                      <RefText d={d} />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                  26.03 이후의 자동 IB 달 탭과 데모-계약 전환에는 같은 번호나 상호가 없습니다. 그 전
                  문의이거나 다른 탭에 있을 수 있습니다.
                </div>
              )}
            </div>
          )}
          <dl style={detail}>
            {columns
              .filter((c) => !isCounter(c) && (open.cells[c.key] ?? '').trim())
              .map((c) => (
                <div key={c.key} style={detailRow}>
                  <dt style={detailKey}>{c.key}</dt>
                  <dd style={detailVal}>{open.cells[c.key]}</dd>
                </div>
              ))}
          </dl>
        </Modal>
      )}
    </div>
  );
}

function RefText({ d }: { d: SheetMirrorDupRefDto }) {
  return (
    <>
      <strong>{d.tabKey === 'demo-conversion' ? '데모-계약 전환' : d.sheetName}</strong>
      <span>{d.rowNumber}행</span>
      <span>{d.date}</span>
      <span>{d.name || '(상호 없음)'}</span>
      {d.flag && <span style={{ color: 'var(--fg-muted)' }}>({d.flag})</span>}
    </>
  );
}

function dupTitle(refs: SheetMirrorDupRefDto[] | undefined): string {
  if (!refs?.length) return '중복이지만 같은 번호나 상호인 줄을 찾지 못했습니다';
  return `${refs[0]!.by === 'phone' ? '같은 전화번호' : '같은 상호'}로 겹친 문의:\n${refs
    .map((d) => `${refLabel(d)} ${d.date} ${d.name}`)
    .join('\n')}`;
}

/** 칸 값별 건수(많은 것 먼저) — 빈 값은 뺀다. */
function countBy(rows: SheetMirrorRowDto[], key: string): [string, number][] {
  const m = new Map<string, number>();
  for (const r of rows) {
    const v = (r.cells[key] ?? '').trim();
    if (v) m.set(v, (m.get(v) ?? 0) + 1);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

/** '2026-10' → '26.10' (시트 탭 이름과 같은 모양) */
function monthLabel(month: string): string {
  return `${month.slice(2, 4)}.${month.slice(5, 7)}`;
}

function agoText(iso: string): string {
  const min = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (min < 1) return '방금';
  if (min < 60) return `${min}분 전`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h}시간 전` : `${Math.floor(h / 24)}일 전`;
}

const page: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  // 좌우는 레이아웃 여백(24)만 — 화면 여백이 겹쳐 48 이던 것을 반으로(2026-10-23 오너 "여기 여백을 반으로")
  // 여백은 레이아웃 여백(24)만 — 위쪽도 다른 화면과 같게(2026-10-23 오너 "윗쪽 간격도 다른 탭들에 비해 좀 큰거같은데")
  padding: 0,
  minWidth: 0,
};
const notice: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  fontSize: 13,
};
const chip: CSSProperties = {
  height: 28,
  padding: '0 12px',
  borderRadius: 999,
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
  cursor: 'pointer',
};
const chipOn: CSSProperties = {
  background: 'var(--brand)',
  border: '1px solid var(--brand)',
  color: 'var(--brand-on)',
};
const kpi: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  minWidth: 110,
  padding: '8px 12px',
  borderRadius: 'var(--r-card)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  cursor: 'pointer',
  textAlign: 'left',
};
const kpiOn: CSSProperties = {
  // 고른 칸은 옅은 바탕 + 얇은 테두리(2026-10-23 오너 "진한 테두리로 남는거 너무 이상").
  // borderColor 만 쓰면 풀 때 React 가 색만 지워 테두리가 글자색(검정)으로 남는다 — 테두리 전체를 준다.
  border: '1px solid color-mix(in srgb, var(--brand) 45%, var(--border-default))',
  background: 'color-mix(in srgb, var(--brand) 8%, var(--bg-surface))',
};
const dim: CSSProperties = { opacity: 0.38 };
const kpiLabel: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  color: 'var(--fg-muted)',
};
/** '→ 26.09 5행' — 누르면 그 줄을 강조 */
const dupRefBtn: CSSProperties = {
  border: 'none',
  background: 'none',
  padding: '0 0 0 3px',
  font: 'inherit',
  color: 'var(--brand)',
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
/** 강조한 중복 상대 줄 */
const litCell: CSSProperties = {
  background: 'color-mix(in srgb, var(--brand) 16%, var(--bg-surface))',
  boxShadow: 'inset 0 2px 0 var(--brand), inset 0 -2px 0 var(--brand)',
};
const dupBox: CSSProperties = {
  padding: '10px 12px',
  marginBottom: 12,
  borderRadius: 'var(--radius-sm)',
  background: 'color-mix(in srgb, var(--brand) 6%, var(--bg-surface))',
  fontSize: 13,
};
const refBtn: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  padding: '4px 8px',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  textAlign: 'left',
  textDecoration: 'none',
  cursor: 'pointer',
};
const checkLabel: CSSProperties = { display: 'inline-flex', gap: 6, alignItems: 'center' };
const linkBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-default)',
  color: 'var(--fg-default)',
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
};
const detail: CSSProperties = { margin: 0, display: 'flex', flexDirection: 'column', gap: 6 };
const detailRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr',
  gap: 12,
  fontSize: 13,
};
const detailKey: CSSProperties = { color: 'var(--fg-muted)', margin: 0 };
const detailVal: CSSProperties = { margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
